<template>
  <div class="onboarding-steps">
    <!-- Abandon button (top-left) -->
    <button
      class="onboarding-abandon-btn"
      :title="$t('onboarding.common.abandon')"
      @click="handleAbandon"
    >
      <span aria-hidden="true">&times;</span>
    </button>

    <!-- Step 1: Networks choice -->
    <div
      class="step"
      :class="{
        active: currentStep === 'networks',
        completed: isStepCompleted('networks')
      }"
      @click="navigateToStep('networks')"
    >
      <div class="step-icon">
        <img
          src="@/components/onboarding/assets/icons/network-icon.svg"
          alt="Réseaux"
        />
      </div>
      <div class="step-content">
        <div class="step-title">{{ $t('onboarding.steps.networks_title') }}</div>
        <div class="step-subtitle">{{ $t('onboarding.steps.networks_subtitle') }}</div>
      </div>
    </div>

    <span class="step-breadcrumb"></span>

    <!-- Step 2: Features choice -->
    <div
      class="step"
      :class="{
        active: currentStep === 'features',
        completed: isStepCompleted('features')
      }"
      @click="navigateToStep('features')"
    >
      <div class="step-icon">
        <img
          src="@/components/onboarding/assets/icons/features-icon.svg"
          alt="Fonctionnalités"
        />
      </div>
      <div class="step-content">
        <div class="step-title">{{ $t('onboarding.steps.features_title') }}</div>
        <div class="step-subtitle">{{ $t('onboarding.steps.features_subtitle') }}</div>
      </div>
    </div>

    <span class="step-breadcrumb"></span>

    <!-- Step 3: Your pages -->
    <div
      class="step"
      :class="{
        active: currentStep === 'pages',
        completed: isStepCompleted('pages'),
        error: isPageLimitReached
      }"
    >
      <div class="step-icon">
        <img
          src="@/components/onboarding/assets/icons/logout-icon.svg"
          alt="Pages"
        />
      </div>
      <div class="step-content">
        <div class="step-title">{{ $t('onboarding.steps.pages_title') }}</div>
        <div class="step-subtitle">
          {{ $t('onboarding.steps.pages_subtitle', { count: totalSelectedPages, max: userPlanPageQuota }) }}
        </div>
      </div>
      <div class="step-badge" v-if="isPageLimitReached">
        <span class="badge-alert" :data-tooltip="$t('onboarding.steps.pages_quota_reached')">●</span>
      </div>
    </div>

    <!-- Sub-steps: Selected networks -->
    <div class="step-children" v-if="selectedNetworks.length > 0">
      <div
        class="step-child"
        v-for="network in selectedNetworks"
        :key="network"
        :class="{
          active: currentStep === 'pages' && currentNetwork === network
        }"
        @click="navigateToNetwork(network)"
      >
      <div class="step-icon">
        <img :src="getNetworkIcon(network)" :alt="getNetworkName(network)" />
      </div>
      <div class="step-content">
        <div class="step-title">{{ getNetworkName(network) }}</div>
      </div>
      <div class="step-badge" v-if="getConnectedCount(network) > 0">
        {{ getConnectedCount(network) }}
      </div>
    </div>
  </div>

    <!-- <span class="step-breadcrumb"></span> -->

    <!-- Step 4: Summary -->
    <div
      class="step"
      :class="{
        active: currentStep === 'summary',
        completed: isStepCompleted('summary')
      }"
      @click="navigateToStep('summary')"
    >
      <div class="step-icon">
        <img
          src="@/components/onboarding/assets/icons/post-icon.svg"
          alt="Résumé"
        />
      </div>
      <div class="step-content">
        <div class="step-title">{{ $t('onboarding.steps.summary_title') }}</div>
        <div class="step-subtitle">{{ $t('onboarding.steps.summary_subtitle') }}</div>
      </div>
    </div>
    
    <LoadingSpinner :show="showSpinner" :message="abandonMessage" hide-percent />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { clearOnboardingFlags } from '../utils/onboardingPersistence';
import { getNetworkName as _getNetworkName, getNetworkIcon } from '../utils/networkMaps';
import User from '../../mixins/User.vue';
import Notif from '../../mixins/Notif.vue';
import Pages from '../../mixins/Pages.vue';
import LoadingSpinner from './LoadingSpinner.vue';
import Lock from '@/utils/Lock.js';

export default {
  name: 'OnboardingSteps',

  components: {
    LoadingSpinner
  },

  mixins: [User, Notif, Pages],

  props: {
    currentStep: {
      type: String,
      required: true,
      validator: value =>
        ['networks', 'features', 'pages', 'natives', 'extensions', 'summary'].includes(value)
    },
    currentNetwork: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      showSpinner: false,
      abandonMessage: ''
    }
  },

  computed: {
    ...mapState('onboarding', [
      'selectedNetworks',
      'selectedFeatures',
      'connectedPages',
      'selectedPageIds',
      'preExistingPageIds'
    ]),
    ...mapGetters('onboarding', [
      'totalConnectedPages'
    ]),

    // Computed locally instead of Vuex function-style getter for proper Vue 2 reactivity.
    // Counts ALL pre-existing pages (from dashboard, regardless of selected networks)
    // PLUS newly connected pages during onboarding (not already pre-existing).
    // This ensures quota reflects TOTAL connected pages, not just selected networks.
    totalSelectedPages() {
      let count = 0
      
      // Safety check: ensure objects exist
      if (!this.preExistingPageIds || !this.selectedPageIds) {
        return 0
      }
      
      const selectedNetworks = this.selectedNetworks || []
      
      // Exhaustive set of networks
      const allNetworks = new Set([
        ...Object.keys(this.preExistingPageIds),
        ...Object.keys(this.selectedPageIds)
      ])
      
      allNetworks.forEach(network => {
        if (selectedNetworks.includes(network)) {
          // If network is selected in onboarding, count user's current selections
          count += (this.selectedPageIds[network] || []).length
        } else {
          // If network is NOT selected in onboarding, count pre-existing pages
          count += (this.preExistingPageIds[network] || []).length
        }
      })
      
      return count
    },

    // Calculate user's page quota based on their plan
    userPlanPageQuota() {
      // If user profile is not loaded yet, do not restrict quota to avoid race conditions
      if (this.user === undefined) return 999
      const plan = this.getUserPlan || 0 // Default to free plan if no user
      const quotas = Lock.rs_limit // [1, 10, 60, 250] — social accounts limit (name is misleading)
      return quotas[plan] || 10 // Default to 10 if plan not found
    },

    isPageLimitReached() {
      return this.totalSelectedPages >= this.userPlanPageQuota
    }
  },

  methods: {
    async handleAbandon() {
      console.log('[OnboardingSteps] User abandoned onboarding')
      
      this.showSpinner = true
      this.abandonMessage = this.$t('onboarding.common.cancelling') || 'Annulation de l\'onboarding en cours...'
      
      try {
        // Gather all newly connected page IDs before store reset
        const newlyConnected = this.$store.state.onboarding.newlyConnectedPageIds || {}
        const preExisting = this.$store.state.onboarding.preExistingPageIds || {}
        const deletePromises = []
        
        Object.keys(newlyConnected).forEach(network => {
          const ids = newlyConnected[network] || []
          const preExistingIds = preExisting[network] || []
          ids.forEach(pageId => {
            // Strictly protect pre-existing pages
            if (pageId && !preExistingIds.includes(pageId)) {
              console.log(`[OnboardingSteps] Deleting page on abandon: ${pageId} (${network})`)
              if (typeof this.deletePage === 'function') {
                deletePromises.push(
                  this.deletePage(pageId).catch(err => {
                    console.error(`[OnboardingSteps] Failed to delete page ${pageId}:`, err)
                  })
                )
              }
            }
          })
        })
        
        if (deletePromises.length > 0) {
          console.log(`[OnboardingSteps] Awaiting deletion of ${deletePromises.length} pages on abandon...`)
          await Promise.allSettled(deletePromises)
          console.log('[OnboardingSteps] All newly connected pages deleted')
        }
      } catch (err) {
        console.error('[OnboardingSteps] Error during abandon cleanup:', err)
      } finally {
        // Reset all onboarding store state (also sets isOnboarding = false)
        this.$store.commit('onboarding/RESET_ONBOARDING')
        // Mark onboarding as skipped to prevent redirect loop
        localStorage.setItem('onboardingSkipped', 'true')
        // Clean up onboarding localStorage flags
        clearOnboardingFlags()
        
        // Redirect to dashboard
        this.$router.push({ name: 'dash' })
      }
    },

    navigateToStep(step) {
      // Block navigation to networks or features if quota exceeded
      if ((step === 'networks' || step === 'features') && this.totalSelectedPages > this.userPlanPageQuota) {
        this.notify({
          title: `${this.$t('onboarding.connect_pages.quota_status')}: ${this.totalSelectedPages}/${this.userPlanPageQuota}`,
          text: this.$t('onboarding.connect_pages.quota_exceeded') + ' ' + this.$t('onboarding.connect_pages.quota_action_deselect'),
          color: 'warning',
          time: 5000
        })
        return
      }

      if (step === 'networks') {
        this.$router.push({ name: 'onboarding-networks' })
      } else if (step === 'features') {
        if (this.selectedNetworks.length > 0) {
          this.$router.push({ name: 'onboarding-features' })
        }
      } else if (step === 'summary') {
        // Use totalConnectedPages (all pages) for navigation check
        if (this.totalConnectedPages > 0) {
          this.$router.push({ name: 'onboarding-summary' })
        }
      }
    },

    navigateToNetwork(network) {

      if (this.currentStep === 'pages') {
        this.$emit('network-selected', network)
      } else if (this.currentStep === 'summary') {
        // From summary, redirect to the connection page for that specific network
        console.log('[OnboardingSteps] Navigating from summary to network:', network)
        this.$router.push({ 
          name: 'onboarding-connectPages',
          params: { network: network }
        })
      }
    },

    isStepCompleted(step) {
      if (step === 'networks') return this.selectedNetworks.length > 0
      if (step === 'features') return this.selectedFeatures.length > 0
      if (step === 'pages') return this.totalConnectedPages > 0
      return false
    },

    getNetworkIcon(network) {
      return getNetworkIcon(network)
    },

    getNetworkName(network) {
      if (network === 'google') return 'Business Profile'
      return _getNetworkName(network)
    },

    getConnectedCount(network) {
      // Count only selected pages
      const selectedIds = this.selectedPageIds[network] || []
      return selectedIds.length
    },
  }
}
</script>

<style lang="scss" scoped>
@import '../variables.scss';

.onboarding-steps {
  position: relative;
  width: 40%;
  background: $bg-onboarding-step;
  border-right: 1px solid #e5e7eb;
  padding: 40px 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-height: 100vh;
  overflow-y: auto;

  &::after {
    content: '';
    display: block;
    height: 80px;
    flex-shrink: 0;
  }
}

.onboarding-abandon-btn {
  position: absolute;
  top: 20px;
  left: 20px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: transparent;
  color: #9ca3af;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
    color: #374151;
  }
}

.step {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 6px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
  }

  &.active {
    background: #f3f4f6;
    width: fit-content;

    .step-content {
      flex: 0 1 auto;
    }
  }

  &.completed {
    .step-icon img {
      opacity: 1;
    }
  }

  &.error {
    width: fit-content;

    .step-content {
      flex: 0 1 auto;
    }

    .step-subtitle {
      color: #ef4444;
    }
  }
}

.step-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  background: #fff;
  border-radius: 4px;

  img {
    width: 65%;
    height: 65%;
    object-fit: contain;
  }
}

.step-breadcrumb {
  border-left: 2px solid #e5e7eb;
  height: 40px;
  margin-left: 32px;
}

.step-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: fit-content;
  gap: 1px;
}

.step-title {
  font-size: 15px;
  font-weight: 600;
  color: $secondary-text-color;
  line-height: 1.4;
}

.step-subtitle {
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.4;
}

.step-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background: $bg-ui-selected;
  color: $primary-color;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;

  .badge-alert {
    position: relative;
    color: #ef4444;
    font-size: 16px;
    cursor: default;

    // CSS tooltip (same pattern as FeatureCard.vue)
    &[data-tooltip]:not([data-tooltip=""]) {
      &::before {
        content: attr(data-tooltip);
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-50%) translateY(-4px);
        padding: 6px 10px;
        background: #424242;
        color: #fff;
        font-size: 12px;
        font-weight: 500;
        border-radius: 6px;
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.1s ease, transform 0.1s ease;
        z-index: 1000;
      }

      &::after {
        content: '';
        position: absolute;
        bottom: calc(100% - 2px);
        left: 50%;
        transform: translateX(-50%) translateY(-4px);
        border: 6px solid transparent;
        border-top-color: #424242;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.1s ease, transform 0.1s ease;
        z-index: 1000;
      }

      &:hover::before,
      &:hover::after {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    }
  }
}

.step-children {
  display: flex;
  flex-direction: column;
  margin-left: 32px;
  padding-left: 14px;
  border-left: 2px solid #e5e7eb;
}

.step-child {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  // width: fit-content;
  width: 160px;

  &:hover {
    background: #f9fafb;
  }

  &.active {
    background: #f3f4f6;
  }

  .step-icon {
    background: none;
    width: 20px;
    height: 20px;

    img {
      width: 100%;
      height: 100%;
    }
  }

  .step-title {
    font-size: 14px;
    font-weight: 500;
    margin-left: 10px; 
  }

  .step-badge {
    min-width: 20px;
    height: 20px;
    font-size: 11px;
  }
}

@media (max-width: 950px) {
  .onboarding-steps {
    padding: 40px 40px;
  }
}

@media (max-width: 800px) {
  .onboarding-steps {
    padding: 40px 20px;
  }

  .step-badge {
    display: none;
  }
}
</style>
